import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { useToast } from '../../../components/Toast';
import { Dropdown } from 'primereact/dropdown';
import samplePageService from './sample-page-service';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import "jspdf-autotable";
import { TieredMenu } from "primereact/tieredmenu";
import { exportExcel, exportPDF } from "../../../components/Export";

const defaultFilters: DataTableFilterMeta = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    'fullName': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    representative: { value: null, matchMode: FilterMatchMode.IN },
    date: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    balance: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    status: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    activity: { value: null, matchMode: FilterMatchMode.BETWEEN },
    verified: { value: null, matchMode: FilterMatchMode.EQUALS },
};

interface RecordFilter {
    [key: string]: string | boolean | Date; // Adjust types as needed
}

const SamplePageList: React.FC = () => {
    const [gridDataSource, setGridDataSource] = useState<any[]>([]);
    const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
    const navigate = useNavigate();
    const [clientState, setClientState] = useState<any>(null);
    const [isValid, setIsValid] = useState<boolean>(true);
    const { showToast } = useToast();
    const menuRef = useRef<TieredMenu>(null);
    const [recordFilter, setRecordFilter] = useState<RecordFilter>({
        dateFrom: new Date(),
        dateTo: new Date(),
        status: 'TST'
    });

    const [statusDropdown, setStatusDropdown] = useState<any[]>([]);
    const statusDropdownOptions = useMemo(() => statusDropdown, [statusDropdown]);

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await samplePageService.getClientState();
                setClientState(clientStateItem);

                samplePageService.getDropdown('BML', 'TEST_STATUS').then((data) => {
                    setStatusDropdown(data);
                });
            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);

    useEffect(() => {
        if (clientState) {
            const fetchUserList = async () => {

                if (recordFilter.dateFrom !== null && recordFilter.dateTo !== null) {
                    if (recordFilter.dateFrom > recordFilter.dateTo) {
                        setIsValid(false);
                    } else {
                        setIsValid(true);
                        try {

                            //Add filter
                            recordFilter.facilityCode = clientState.facilityCode;

                            samplePageService.findAllDonor(recordFilter).then((data) => {
                                setGridDataSource(data);
                            });
                        } catch (error) {
                            showToast(clientState.error);
                        }
                    }
                }
            };

            fetchUserList();
        }
    }, [clientState, recordFilter]);



    function handleCreate(): void {
        navigate(`/uikit/sample-page-edit`);
    }

    const handleEdit = (item: any) => {
        navigate('/uikit/sample-page-edit', { state: { selectedItem: item } });
    };

    const handleView = (item: any) => {
        navigate('/uikit/sample-page-view', { state: { item: item } });
    };

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters(defaultFilters);
        setGlobalFilterValue('');
    }

    const header = () => {

        const formatDate = (date: any): string => {
            if (!date || typeof date === "boolean") return "Invalid Date";
            return new Date(date).toLocaleDateString("en-GB"); 
        };
        
        const fileName = `Sample Page ${formatDate(recordFilter.dateFrom)} - ${formatDate(recordFilter.dateTo)}`;

        const exportOptions: any[] = [
            {
                label: "Export to Excel",
                icon: "pi pi-file-excel",
                command: () => exportExcel(fileName),
            },
            {
                label: "Export to PDF",
                icon: "pi pi-file-pdf",
                command: () => exportPDF(fileName),
            },
        ];

        return (
            <div>
                <div className="flex justify-content-between">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.3em' }}>
                        <span className="p-float-label">
                            <InputText type="text" id="inputtext" value={globalFilterValue} onChange={onGlobalFilterChange} />
                            <label htmlFor="inputtext">Keyword Search</label>
                        </span>
                    </div>
                    <div>
                        <TieredMenu model={exportOptions} popup ref={menuRef} />
                        <Button label="Excel" icon="pi pi-file-excel" className="p-button-success" style={{ marginRight: '.5em' }} onClick={() => exportPDF(fileName)} />
                        <Button label="PDF" icon="pi pi-file-pdf" className="p-button-danger" style={{ marginRight: '.5em' }} onClick={() => exportPDF(fileName)} />
                        <Button label="New" icon="pi pi-plus" style={{ marginRight: '.5em' }} onClick={() => handleCreate()} />

                    </div>
                </div>
            </div>
        );
    };

    const actionTemplate = (rowData: any) => (
        <div style={{ display: 'flex', gap: '5px' }}>
            <Button
                label="Edit"
                icon="pi pi-pencil"
                className="p-button-warning p-button-sm "
                style={{ padding: '5px 5px', fontSize: '10px' }}
                onClick={() => handleEdit(rowData)}
            />
        </div>
    );

    const recordFilterTemplate = () => {
        const handleRecordFilterChange = (e: any) => {
            const { id, value } = e.target;
            setRecordFilter({
                ...recordFilter,
                [id]: value
            });

            console.log(recordFilter)
        };

        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '2em' }}>
                    <div className="p-float-label" style={{ paddingTop: '0.5rem' }}>
                        <Calendar
                            id="dateFrom"
                            value={recordFilter.dateFrom as Date}
                            onChange={handleRecordFilterChange}
                            dateFormat="dd M yy"
                            showIcon
                            className={isValid ? '' : 'p-invalid'}
                        >
                        </Calendar>
                        <label style={{ paddingTop: '0.5rem', paddingBottom: '0.2rem' }}>Date From</label>
                    </div>
                    <div className="p-float-label" style={{ paddingTop: '0.5rem', paddingBottom: '0.2rem' }}>
                        <Calendar
                            id="dateTo"
                            value={recordFilter.dateTo as Date}
                            onChange={handleRecordFilterChange}
                            dateFormat="dd M yy"
                            showIcon
                        >
                        </Calendar>
                        <label style={{ paddingTop: '0.5rem' }}>Date To</label>
                    </div>
                    <div className="p-float-label" style={{ paddingTop: '0.5rem', paddingBottom: '0.2rem' }}>

                        <Dropdown
                            id="status"
                            value={recordFilter.status}
                            onChange={handleRecordFilterChange}
                            options={statusDropdownOptions}
                            optionLabel="fullDesc"
                            placeholder="Select One"
                            optionValue="referenceCode"
                            showClear
                        >
                            <label style={{ paddingTop: '0.5rem', paddingBottom: '0.2rem' }}>Status</label>
                        </Dropdown>

                    </div>
                </div>
                {!isValid && (
                    <small id="dateFrom-help" className="p-error">
                        Date From must be earlier than or equal to Date To
                    </small>
                )}
            </div>
        )
    }

    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>Sample Page</h5>
                    <BreadcrumbComponent />
                </div>

                <div className='card'>
                    {/* Filter Date From and Date To */}
                    <Toolbar start={recordFilterTemplate}></Toolbar>
                    <div><br></br></div>
                    <>
                        <DataTable
                            value={gridDataSource}
                            showGridlines
                            scrollable
                            scrollHeight='auto'
                            paginator rows={10}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            filters={filters}
                            globalFilterFields={['fullName', 'barcode', 'idNo']}
                            header={header}
                            sortField='modifiedDate'
                            sortOrder={-1}
                        >
                            <Column
                                header="No."
                                style={{ width: '5%' }}
                                body={(_, { rowIndex }) => rowIndex + 1} // Render row number
                            ></Column>
                            <Column field="insertDate" header="Register Date" body={(rowData) => new Date(rowData.insertDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', })}></Column>
                            <Column field="fullDesc" header="Access Right Name"></Column>
                            <Column field="facilityCode" header="Facility" ></Column>
                            <Column field="recordStatus" header="Status" body={(rowData) => rowData.recordStatus === '1' ? 'Active' : 'Inactive'}></Column>
                            <Column body={actionTemplate} header="Actions" style={{ width: '20%' }} ></Column>
                        </DataTable>
                    </>
                </div>
            </div>
        </div >
    );
};

export default SamplePageList;
